import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Star, ArrowUp, ArrowDown, Minus, RefreshCw, X, Trophy } from "lucide-react";
import { sr } from "../../lib/sr";

function Delta({ value, suffix = "" }) {
  if (value == null || value === 0) return <span className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center" }}><Minus size={11} /></span>;
  const up = value > 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: up ? "#067647" : "#B42318", display: "inline-flex", alignItems: "center", gap: 1 }}>
      {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(value)}{suffix}
    </span>
  );
}

const Rating = ({ r }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontWeight: 700, color: "var(--navy)" }}>
    <Star size={13} style={{ color: "#E0A94A", fill: "#E0A94A" }} /> {r != null ? Number(r).toFixed(1) : "—"}
  </span>
);

export default function CompetitorCompare({ tenantId, title = "Competitor watch — rating & review trend" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => sr.pinnedCompetitors(tenantId).then(setData).catch(() => setData({ pinned: [], you: {} }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  if (!data) return null;
  const you = data.you || {};
  const pins = data.pinned || [];

  const refresh = async () => {
    setBusy(true);
    try { const r = await sr.refreshPinnedCompetitors(tenantId); toast.success(`Refreshed ${r.refreshed} competitor${r.refreshed === 1 ? "" : "s"}`); load(); }
    catch { toast.error("Refresh failed"); } finally { setBusy(false); }
  };
  const unpin = async (pid) => {
    try { await sr.unpinCompetitor(pid, tenantId); toast.success("Unpinned"); load(); }
    catch { toast.error("Could not unpin"); }
  };
  const saveNote = async (pid, note) => {
    try { await sr.noteCompetitor(pid, note, tenantId); }
    catch { toast.error("Could not save note"); }
  };

  const youRating = you.rating != null ? Number(you.rating) : null;

  return (
    <div className="sr-card" style={{ marginTop: 18 }} data-testid="competitor-compare">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="sr-eyebrow" style={{ margin: 0 }}>{title}</div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={refresh} disabled={busy} data-testid="cc-refresh">
          {busy ? <Loader2 size={13} className="sr-spin" /> : <RefreshCw size={13} />} Refresh
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, padding: "8px 12px", background: "var(--blue-light, #F2FBF5)", borderRadius: 8 }} data-testid="cc-you">
        <span className="sr-badge sr-badge-green" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Trophy size={11} /> You</span>
        <strong style={{ color: "var(--navy)" }}>{you.name || "Your business"}</strong>
        <Rating r={youRating} />
        {you.rating_count != null && <span className="sr-muted" style={{ fontSize: 12 }}>({you.rating_count} reviews)</span>}
      </div>

      {pins.length === 0 ? (
        <p className="sr-muted" style={{ fontSize: 13, marginTop: 12 }} data-testid="cc-empty">
          No competitors pinned yet. Pin competitors from the Competitor Studio to track their rating and review-count trend here over time.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          {pins.map((c) => {
            const beatingYou = youRating != null && c.rating != null && Number(c.rating) > youRating;
            return (
              <div key={c.place_id} data-testid={`cc-row-${c.place_id}`}
                style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr auto", gap: 10, alignItems: "center", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 8, background: "#fff" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <strong style={{ color: "var(--navy)", fontSize: 14 }}>{c.name || "Competitor"}</strong>
                    {beatingYou && <span className="sr-badge sr-badge-amber" style={{ fontSize: 10 }} data-testid={`cc-ahead-${c.place_id}`}>Rated higher</span>}
                  </div>
                  {c.address && <div className="sr-muted" style={{ fontSize: 12 }}>{c.address}</div>}
                  <input className="sr-input" style={{ marginTop: 6, fontSize: 12, padding: "6px 8px" }} defaultValue={c.note || ""}
                    placeholder="Notes (e.g. offers you spotted)…" onBlur={(e) => e.target.value !== (c.note || "") && saveNote(c.place_id, e.target.value)}
                    data-testid={`cc-note-${c.place_id}`} />
                </div>
                <div>
                  <div className="sr-muted" style={{ fontSize: 11 }}>Rating</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Rating r={c.rating} /> <Delta value={c.rating_delta} /></div>
                </div>
                <div>
                  <div className="sr-muted" style={{ fontSize: 11 }}>Reviews</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700, color: "var(--navy)" }}>{c.rating_count != null ? Number(c.rating_count).toLocaleString() : "—"}</span>
                    <Delta value={c.count_delta} />
                  </div>
                  {c.since && <div className="sr-muted" style={{ fontSize: 10 }}>since {c.since}</div>}
                </div>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => unpin(c.place_id)} data-testid={`cc-unpin-${c.place_id}`} title="Unpin">
                  <X size={14} />
                </button>
              </div>
            );
          })}
          <p className="sr-muted" style={{ fontSize: 11, marginTop: 2 }}>
            Rating &amp; review counts come from Google and refresh weekly — trend arrows compare against when you first pinned each competitor.
          </p>
        </div>
      )}
    </div>
  );
}
